// src/pages/EventDetails.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Stack,
  Button,
  Chip,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Container,
  Divider,
  Paper,
  Avatar,
  Skeleton,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ShareIcon from "@mui/icons-material/Share";
import ReportIcon from "@mui/icons-material/Report";
import EditIcon from "@mui/icons-material/Edit";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import EventSeatIcon from "@mui/icons-material/EventSeat";
import AccessibilityNewIcon from "@mui/icons-material/AccessibilityNew";
import LocalParkingIcon from "@mui/icons-material/LocalParking";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useParams, useNavigate } from "react-router-dom";
import { eventService } from "../services/resources.service";
import LoadingSpinner from "../components/LoadingSpinner";
import DiscoveryMap from "../components/DiscoveryMap";
import EventReviewDialog from "../components/EventReviewDialog";
import { formatDateTime, formatEventPrice, formatTime, getMessage, hasEventPrice, useLocale } from "../i18n/locale";
import { canEditEvent, canRateEventLineup } from "../services/authRoles";
import { BANNER_IMAGE_FALLBACK, imageFallbackHandler } from "../services/profileImages";

async function fetchEventById(id) {
  return eventService.get(id);
}

async function fetchSimilarEvents(id) {
  return eventService.similar(id);
}

function futureEvents(events, now = Date.now()) {
  return (events ?? []).filter((candidate) => {
    const startAt = new Date(candidate?.startAt).getTime();
    return Number.isFinite(startAt) && startAt > now;
  });
}

// ---------------- Small UI helpers ----------------
const Price = ({ event }) => {
  const locale = useLocale();
  return (
    <Typography variant="body2" sx={{ fontWeight: 700 }}>
      {formatEventPrice(event, locale)}
    </Typography>
  );
};

function InfoRow({ icon, label, value, action }) {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{ py: 1, borderBottom: 1, borderColor: "divider" }}
    >
      {icon}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="subtitle2" noWrap={!action}>
          {value}
        </Typography>
      </Box>
      {action}
    </Stack>
  );
}

function LineupList({ lineup, locale, onArtist, onRate, showRate }) {
  return (
    <Stack spacing={1}>
      {lineup.map((a) => {
        const rating = Number(a.rating);
        const hasRating = Number.isFinite(rating);
        return (
        <Stack
          key={a.id}
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ p: 1, borderRadius: 2, bgcolor: "background.paper", border: 1, borderColor: "divider" }}
        >
          <Avatar src={a.avatar || undefined} alt={a.name}>
            {a.name?.[0]}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap>
              {a.name}
            </Typography>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <StarRoundedIcon color={hasRating ? "warning" : "disabled"} sx={{ fontSize: 16 }} />
              <Typography variant="caption">
                {hasRating ? rating.toFixed(1) : "—"}
              </Typography>
            </Stack>
          </Box>
          <Stack direction="row" spacing={0.5}>
            <Button size="small" onClick={() => onArtist?.(a.id)}>
              View
            </Button>
            {showRate && (
              <Button size="small" variant="outlined" onClick={() => onRate?.(a)}>
                {getMessage(locale, "eventRateArtist")}
              </Button>
            )}
          </Stack>
        </Stack>
        );
      })}
    </Stack>
  );
}

function SimilarEventCard({ evt, onOpen }) {
  const locale = useLocale();
  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardMedia component="img" height="120" image={evt.poster || BANNER_IMAGE_FALLBACK} onError={imageFallbackHandler(BANNER_IMAGE_FALLBACK)} alt={evt.title} loading="lazy" />
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary">
          {formatDateTime(evt.startAt, locale)}
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
          {evt.title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {evt.venue?.name} ({evt.venue?.city})
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
          {evt.tags?.map((t) => (
            <Chip key={t} label={t} size="small" />
          ))}
        </Stack>
      </CardContent>
      <Box sx={{ px: 2, pb: 2 }}>
        <Button fullWidth variant="outlined" onClick={() => onOpen(evt.id)}>
          Open
        </Button>
      </Box>
    </Card>
  );
}

// ---------------- Page ----------------
export default function EventDetails({ user }) {
  const localeContext = useLocale();
  const { locale } = localeContext;
  const { id } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [similar, setSimilar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "info" });
  const [reviewArtist, setReviewArtist] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const e = await fetchEventById(id);
      const s = await fetchSimilarEvents(id);
      if (!active) return;
      setEvent(e);
      setSimilar(futureEvents(s));
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [id]);

  const dtStart = useMemo(
    () => (event ? new Date(event.startAt) : null),
    [event]
  );
  const dtEnd = useMemo(() => (event ? new Date(event.endAt) : null), [event]);
  const lineupRatings = useMemo(
    () =>
      (event?.lineup ?? [])
        .map((artist) => Number(artist.rating))
        .filter(Number.isFinite),
    [event]
  );

  const saveToCalendar = () => {
    if (!event) return;
    const start = new Date(event.startAt)
      .toISOString()
      .replace(/[-:]/g, "")
      .split(".")[0] + "Z";
    const end = event.endAt
      ? new Date(event.endAt).toISOString().replace(/[-:]/g, "").split(".")[0] + "Z"
      : start;
    const desc = `${event.description?.replace(/\r?\n/g, " ")}${
      event.venue?.name ? ` @ ${event.venue.name}` : ""
    }`;
    const ics = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:Band-Joe",
      "BEGIN:VEVENT",
      `UID:${event.id}@band-joe`,
      `DTSTAMP:${start}`,
      `DTSTART:${start}`,
      `DTEND:${end}`,
      `SUMMARY:${event.title}`,
      `DESCRIPTION:${desc}`,
      `LOCATION:${event.venue?.address || event.venue?.name || ""}`,
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n");
    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${event.title}.ics`;
    a.click();
    URL.revokeObjectURL(url);
    setSnack({ open: true, msg: "Saved to calendar", severity: "success" });
  };

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: event?.title ?? "Event", url });
      } catch (_) {
        // ignore
      }
    } else {
      await navigator.clipboard.writeText(url);
      setSnack({ open: true, msg: "Link copied to clipboard", severity: "info" });
    }
  };

  const report = () =>
    setSnack({ open: true, msg: "Thanks. Our team will review this event.", severity: "info" });

  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: "background.default" }}>
      <AppBar position="sticky" elevation={0} color="inherit">
        <Toolbar sx={{ gap: 1 }}>
          <IconButton edge="start" aria-label="back" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ fontWeight: 900, letterSpacing: 0.2 }}>
            Event Details
          </Typography>
          <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
            {canEditEvent(user, event) && (
              <Tooltip title={getMessage(locale, "eventEdit")}>
                <IconButton onClick={() => navigate(`/events/${event.id}/edit`)} aria-label={getMessage(locale, "eventEdit")}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Save to calendar">
              <IconButton onClick={saveToCalendar} aria-label="save to calendar">
                <CalendarMonthIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Share">
              <IconButton onClick={share} aria-label="share">
                <ShareIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Report">
              <IconButton onClick={report} aria-label="report">
                <ReportIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      {loading ? (
        <LoadingSpinner minHeight="calc(100dvh - 64px)" size={44} />
      ) : (
      <Container maxWidth="lg" sx={{ py: 2 }}>
        {/* Poster / Skeleton */}
        {loading ? (
          <Skeleton variant="rounded" height={220} sx={{ borderRadius: 3, mb: 2 }} />
        ) : (
          <Card variant="outlined" sx={{ borderRadius: 3, mb: 2 }}>
            <CardMedia
              component="img"
              height="240"
              image={event.poster || BANNER_IMAGE_FALLBACK}
              onError={imageFallbackHandler(BANNER_IMAGE_FALLBACK)}
              alt={`${event.title} poster`}
              loading="lazy"
            />
          </Card>
        )}

        {/* Title + Meta + CTA */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Stack spacing={1.5}>
              {loading ? (
                <>
                  <Skeleton width="60%" height={36} />
                  <Skeleton width="40%" height={24} />
                </>
              ) : (
                <>
                  <Typography variant="h5" fontWeight={900}>
                    {event.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatDateTime(dtStart, localeContext)}{" "}
                    {event.venue?.name ? `• ${event.venue.name} (${event.venue.city})` : ""}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                    {event.tags?.map((t) => (
                      <Chip key={t} label={t} size="small" />
                    ))}
                    {hasEventPrice(event) && (
                      <Chip
                        size="small"
                        icon={<LocalOfferIcon />}
                        label={<Price event={event} />}
                        variant="outlined"
                      />
                    )}
                    {event.ageLimit && <Chip size="small" label={event.ageLimit} />}
                  </Stack>
                </>
              )}

              {/* Actions */}
              {loading ? (
                <Skeleton variant="rounded" height={44} width={260} />
              ) : (
                <Stack direction="row" spacing={1}>
                  {event.ticketUrl && (
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<OpenInNewIcon />}
                      onClick={() => window.open(event.ticketUrl, "_blank", "noopener,noreferrer")}
                    >
                      {getMessage(locale, event.price === 0 ? "eventGetTickets" : "eventBuyTickets")}
                    </Button>
                  )}
                  {event.links?.facebook && (
                    <Button
                      component="a"
                      href={event.links.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outlined"
                      size="large"
                      startIcon={<OpenInNewIcon />}
                    >
                      {getMessage(locale, "eventViewOnFacebook")}
                    </Button>
                  )}
                  <Button variant="outlined" size="large" onClick={saveToCalendar} startIcon={<CalendarMonthIcon />}>
                    Save
                  </Button>
                  <Button variant="outlined" size="large" onClick={share} startIcon={<ShareIcon />}>
                    Share
                  </Button>
                </Stack>
              )}

              {/* Description */}
              {loading ? (
                <>
                  <Skeleton height={18} />
                  <Skeleton height={18} width="90%" />
                  <Skeleton height={18} width="75%" />
                </>
              ) : (
                <Typography variant="body1" sx={{ mt: 1 }}>
                  {event.description}
                </Typography>
              )}
            </Stack>
          </Grid>

          {/* Sidebar Info */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper variant="outlined" sx={{ borderRadius: 3, p: 2 }}>
              {loading ? (
                <>
                  <Skeleton height={28} width="40%" />
                  <Skeleton height={20} />
                  <Skeleton height={20} width="80%" />
                </>
              ) : (
                <>
                  <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
                    Event Info
                  </Typography>
                  <InfoRow
                    icon={<CalendarMonthIcon />}
                    label="Time"
                    value={`${formatDateTime(dtStart, localeContext)}${dtEnd ? ` – ${formatTime(dtEnd, localeContext)}` : ""}`}
                  />
                  <InfoRow
                    icon={<LocationOnIcon />}
                    label="Venue"
                    value={`${event.venue?.name ?? "—"} • ${event.venue?.address ?? event.venue?.city ?? ""}`}
                    action={
                      <Button
                        size="small"
                        onClick={() => navigate(`/venues/${event.venue?.id}`)}
                        disabled={!event.venue?.id}
                      >
                        View
                      </Button>
                    }
                  />
                  <InfoRow
                    icon={<AccessibilityNewIcon />}
                    label="Accessibility"
                    value={event.accessibility?.length ? event.accessibility.join(", ") : "—"}
                  />
                  <InfoRow icon={<LocalParkingIcon />} label="Parking" value={event.parking || "—"} />
                  <InfoRow
                    icon={<EventSeatIcon />}
                    label="Capacity"
                    value={(event.capacity ?? event.venue?.capacity) ? `${event.capacity ?? event.venue.capacity}` : "—"}
                  />
                  <InfoRow
                    icon={<PeopleAltIcon />}
                    label="Line-up"
                    value={`${event.lineup?.length ?? 0} artist(s)`}
                    action={
                      event.lineup?.length ? (
                        <Button size="small" onClick={() => document.getElementById("lineup")?.scrollIntoView({ behavior: "smooth" })}>
                          See
                        </Button>
                      ) : null
                    }
                  />
                </>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Map */}
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 3,
            p: 2,
            mt: 2,
          }}
        >
          <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
            Location
          </Typography>
          {loading ? (
            <Skeleton variant="rounded" height={220} sx={{ borderRadius: 2 }} />
          ) : (
            <DiscoveryMap
              items={[event]}
              getCoordinates={(item) => item.venue?.geo}
              getLabel={(item) => item.venue?.name || item.title}
              labelMessageKey="venueMapLabel"
              height={260}
            />
          )}
        </Paper>

        {/* Line-up */}
        <Paper variant="outlined" sx={{ borderRadius: 3, p: 2, mt: 2 }} id="lineup">
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={800}>
              Line-up
            </Typography>
            {!loading && (
              <Stack direction="row" spacing={1} alignItems="center">
                <StarRoundedIcon sx={{ fontSize: 18 }} />
                <Typography variant="body2" color="text.secondary">
                  Avg rating:{" "}
                  {lineupRatings.length
                    ? (
                        lineupRatings.reduce((sum, rating) => sum + rating, 0) / lineupRatings.length
                      ).toFixed(1)
                    : "—"}
                </Typography>
              </Stack>
            )}
          </Stack>

          {loading ? (
            <Stack spacing={1}>
              <Skeleton variant="rounded" height={64} sx={{ borderRadius: 2 }} />
              <Skeleton variant="rounded" height={64} sx={{ borderRadius: 2 }} />
            </Stack>
          ) : event.lineup?.length ? (
            <LineupList
              lineup={event.lineup}
              locale={locale}
              onArtist={(artistId) => navigate(`/artists/${artistId}`)}
              onRate={setReviewArtist}
              showRate={canRateEventLineup(user, event)}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              No artists listed.
            </Typography>
          )}
        </Paper>

        <EventReviewDialog
          open={Boolean(reviewArtist)}
          onClose={() => setReviewArtist(null)}
          targetType="artist"
          targetId={reviewArtist?.id}
          onCreated={() => {
            setSnack({
              open: true,
              msg: getMessage(locale, "reviewSuccess"),
              severity: "success",
            });
          }}
        />

        {/* Similar events */}
        <Paper variant="outlined" sx={{ borderRadius: 3, p: 2, mt: 2 }}>
          <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
            You might also like
          </Typography>
          {loading ? (
            <Grid container spacing={2}>
              {Array.from({ length: 2 }).map((_, i) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
                  <Skeleton variant="rounded" height={220} sx={{ borderRadius: 3 }} />
                </Grid>
              ))}
            </Grid>
          ) : similar?.length ? (
            <Grid container spacing={2}>
              {similar.map((e) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={e.id}>
                  <SimilarEventCard evt={e} onOpen={(eid) => navigate(`/events/${eid}`)} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No similar events found.
            </Typography>
          )}
        </Paper>

        {/* Report / Share / Save notifications */}
        <Snackbar
          open={snack.open}
          autoHideDuration={2200}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity={snack.severity} sx={{ width: "100%" }}>
            {snack.msg}
          </Alert>
        </Snackbar>

        <Box sx={{ height: 16 }} />
      </Container>
      )}
    </Box>
  );
}
